import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar } from "../../components/ui/calendar";
import { PhoneCall, Ruler, FileText, CheckCircle2, Loader2 } from "lucide-react";
import { Reveal, Kicker } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLOTS = [
  { key: "morning", label: "Morning", time: "8:00 – 11:00" },
  { key: "midday", label: "Midday", time: "11:00 – 14:00" },
  { key: "afternoon", label: "Afternoon", time: "14:00 – 17:00" },
];

const STEPS = [
  { icon: PhoneCall, title: "We confirm", desc: "A quick call within one working day to confirm your slot and talk through the project." },
  { icon: Ruler, title: "We visit", desc: "A 30–45 minute site visit — measurements, photos and honest, practical advice." },
  { icon: FileText, title: "You get a fixed quote", desc: "An itemised written quotation within 48 hours. No obligation, no hidden charges." },
];

const inputCls =
  "w-full rounded-none border border-black/15 bg-white px-4 py-3.5 text-sm text-ink placeholder:text-black/30 transition-colors duration-300 focus:border-bronze focus:outline-none focus:ring-1 focus:ring-bronze";

const tomorrow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(0, 0, 0, 0);
  return d;
};

const fmtDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const prettyDate = (d) =>
  d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

export const SurveyBooking = () => {
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", postcode: "", email: "", notes: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const set = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
    if (errors[key]) setErrors({ ...errors, [key]: undefined });
  };

  const validate = () => {
    const errs = {};
    if (!date) errs.date = "Please choose a survey date.";
    if (!slot) errs.slot = "Please choose a time slot.";
    if (form.name.trim().length < 2) errs.name = "Please enter your full name.";
    if (!/^[\d\s()+.-]{7,}$/.test(form.phone.trim())) errs.phone = "Please enter a valid phone number.";
    if (!/^[A-Za-z]{1,2}\d[A-Za-z\d]?\s*\d[A-Za-z]{2}$/.test(form.postcode.trim()))
      errs.postcode = "Enter a valid UK postcode.";
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim()))
      errs.email = "Enter a valid email, or leave it blank.";
    return errs;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/surveys`, { ...form, date: fmtDate(date), slot });
      setStatus("success");
      toast.success("Survey booked — we'll call to confirm within one working day.");
    } catch (err) {
      setStatus("idle");
      toast.error("Could not book your survey. Please try again or call us directly.");
    }
  };

  const reset = () => {
    setDate(null);
    setSlot("");
    setForm({ name: "", phone: "", postcode: "", notes: "" });
    setErrors({});
    setStatus("idle");
  };

  return (
    <section id="survey" data-testid="survey-section" className="bg-white py-24 md:py-36">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-2 lg:gap-20">
        <div>
          <Reveal>
            <Kicker index="08" label="Free Site Survey" />
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display text-5xl font-bold uppercase leading-[0.95] tracking-tight text-ink md:text-6xl">
              Book a Free
              <span className="block text-gold-shine">Site Survey</span>
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-8 max-w-md leading-relaxed text-fog">
              Pick a date and time that suits you — we'll visit, measure up and
              give you a fixed, itemised quotation within 48 hours. Mondays to
              Saturdays, across West London and the surrounding areas.
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="mt-10 space-y-0 border-t border-black/10">
              {STEPS.map((step, i) => {
                const Icon = step.icon;
                return (
                  <div key={step.title} className="flex items-start gap-5 border-b border-black/10 py-6">
                    <span className="font-display text-3xl font-extrabold text-outline-bronze">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="grid h-11 w-11 shrink-0 place-items-center border border-bronze/40">
                      <Icon className="h-5 w-5 text-bronze" strokeWidth={1.8} />
                    </span>
                    <div>
                      <h3 className="font-display text-lg font-bold uppercase tracking-wide text-ink">
                        {step.title}
                      </h3>
                      <p className="mt-1 text-sm leading-relaxed text-fog">{step.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="border border-black/10 bg-coal p-6 md:p-8">
            {status === "success" ? (
              <div data-testid="survey-success" className="flex flex-col items-start gap-6 py-10">
                <span className="grid h-16 w-16 place-items-center border border-bronze/50">
                  <CheckCircle2 className="h-8 w-8 text-bronze" strokeWidth={1.5} />
                </span>
                <h3 className="font-display text-4xl font-bold uppercase tracking-wide text-ink">
                  Survey Booked
                </h3>
                <p className="max-w-md leading-relaxed text-fog">
                  You're booked for{" "}
                  <span className="font-semibold text-ink">{prettyDate(date)}</span>,{" "}
                  {slot.toLowerCase()}. We'll call {form.name.split(" ")[0]} on{" "}
                  {form.phone} within one working day to confirm.
                </p>
                <button
                  data-testid="survey-another-btn"
                  onClick={reset}
                  className="border border-black/25 px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-ink transition-colors duration-300 hover:border-bronze hover:text-bronze"
                >
                  Book another survey
                </button>
              </div>
            ) : (
              <form data-testid="survey-form" onSubmit={submit} noValidate>
                <label className="mb-3 block text-[11px] font-medium uppercase tracking-[0.25em] text-black/50">
                  1 · Choose a date (Mon – Sat)
                </label>
                <div className="flex justify-center border border-black/10 bg-white" data-testid="survey-calendar">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => {
                      setDate(d);
                      if (errors.date) setErrors({ ...errors, date: undefined });
                    }}
                    disabled={[{ before: tomorrow() }, (d) => d.getDay() === 0]}
                    classNames={{
                      day_selected:
                        "bg-ink text-bronze hover:bg-ink hover:text-bronze focus:bg-ink focus:text-bronze",
                    }}
                  />
                </div>
                {errors.date && (
                  <p data-testid="survey-date-error" className="mt-1.5 text-xs text-bronze">{errors.date}</p>
                )}

                <label className="mb-3 mt-7 block text-[11px] font-medium uppercase tracking-[0.25em] text-black/50">
                  2 · Choose a time slot
                </label>
                <div className="grid grid-cols-3 gap-px border border-black/10 bg-black/10">
                  {SLOTS.map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      data-testid={`survey-slot-${s.key}`}
                      onClick={() => {
                        setSlot(`${s.label} · ${s.time}`);
                        if (errors.slot) setErrors({ ...errors, slot: undefined });
                      }}
                      className={`px-2 py-3.5 text-center transition-colors duration-300 ${
                        slot.startsWith(s.label) ? "bg-ink text-bronze" : "bg-white text-ink hover:bg-steel"
                      }`}
                    >
                      <span className="block text-xs font-semibold uppercase tracking-[0.2em]">{s.label}</span>
                      <span className={`mt-1 block text-[11px] ${slot.startsWith(s.label) ? "text-bronze/80" : "text-black/50"}`}>
                        {s.time}
                      </span>
                    </button>
                  ))}
                </div>
                {errors.slot && (
                  <p data-testid="survey-slot-error" className="mt-1.5 text-xs text-bronze">{errors.slot}</p>
                )}

                {date && slot && (
                  <p data-testid="survey-selection" className="mt-4 border border-bronze/30 bg-bronze/10 px-4 py-3 text-xs uppercase tracking-[0.15em] text-ink">
                    Selected: <span className="font-semibold">{prettyDate(date)}</span> — {slot}
                  </p>
                )}

                <label className="mb-3 mt-7 block text-[11px] font-medium uppercase tracking-[0.25em] text-black/50">
                  3 · Your details
                </label>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <input
                      data-testid="survey-name-input"
                      type="text"
                      value={form.name}
                      onChange={set("name")}
                      placeholder="Full name"
                      className={inputCls}
                    />
                    {errors.name && <p data-testid="survey-name-error" className="mt-1.5 text-xs text-bronze">{errors.name}</p>}
                  </div>
                  <div>
                    <input
                      data-testid="survey-phone-input"
                      type="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      placeholder="Phone number"
                      className={inputCls}
                    />
                    {errors.phone && <p data-testid="survey-phone-error" className="mt-1.5 text-xs text-bronze">{errors.phone}</p>}
                  </div>
                  <div>
                    <input
                      data-testid="survey-email-input"
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      placeholder="Email (for a reminder — optional)"
                      className={inputCls}
                    />
                    {errors.email && <p data-testid="survey-email-error" className="mt-1.5 text-xs text-bronze">{errors.email}</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <input
                      data-testid="survey-postcode-input"
                      type="text"
                      value={form.postcode}
                      onChange={set("postcode")}
                      placeholder="Postcode of the property"
                      className={`${inputCls} uppercase`}
                    />
                    {errors.postcode && <p data-testid="survey-postcode-error" className="mt-1.5 text-xs text-bronze">{errors.postcode}</p>}
                  </div>
                  <div className="sm:col-span-2">
                    <textarea
                      data-testid="survey-notes-input"
                      rows={2}
                      value={form.notes}
                      onChange={set("notes")}
                      placeholder="Anything we should know? (optional)"
                      className={`${inputCls} resize-none`}
                    />
                  </div>
                </div>

                <button
                  data-testid="survey-submit-btn"
                  type="submit"
                  disabled={status === "sending"}
                  className="mt-6 flex w-full items-center justify-center gap-3 bg-bronze px-9 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-ink transition-colors duration-300 hover:bg-ink hover:text-bronze disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === "sending" ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Booking
                    </>
                  ) : (
                    "Book My Free Survey"
                  )}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
